export type ManagementDecisionType = "surgical" | "conservative" | "indeterminate";

export type EvidenceItemV2 = {
  title: string;
  authors?: string;
  journal?: string;
  year?: string;
  doi?: string;
  url?: string;
  relevance: string;
};

export type SurgicalTechniqueV2 = {
  title: string;
  indication: string[];
  whenToChoose: string[];
  positioning: string[];
  surgicalApproach: string[];
  stepByStepTechnique: string[];
  implantsAndMaterials: string[];
  advantages: string[];
  limitationsAndRisks: string[];
  possibleComplications: string[];
  consolidationTime: string[];
  planB: string[];
  evidence: EvidenceItemV2[];
};

export type SurgicalPlanV2 = {
  diagnosis: string;
  anatomicalLocation: string;
  aoOtaClassification: string;
  managementDecision: {
    type: ManagementDecisionType;
    rationale: string;
    surgicalCriteria: string[];
    conservativeCriteria: string[];
  };
  surgicalTechniques: SurgicalTechniqueV2[];
  conservativeTreatment: {
    indication: string[];
    immobilizationOrProtection: string[];
    medication: string[];
    rehabilitation: string[];
    followUp: string[];
    expectedRecovery: string[];
    failureCriteria: string[];
    conversionToSurgery: string[];
  };
  postoperativeCare: {
    immobilization: string[];
    rangeOfMotion: string[];
    physiotherapy: string[];
    weightBearing: string[];
    followUpImaging: string[];
    returnToFunction: string[];
    redFlags: string[];
  };
  intraoperativeContingencyPlan: string[];
  differentialDiagnosisAndAssociatedInjuries: string[];
  globalEvidence: EvidenceItemV2[];
};

export type SurgicalPlanV2Validation = {
  valid: boolean;
  repairable: boolean;
  reasons: string[];
};

const forbiddenPhrases = [
  "nao informado pela ia",
  "não informado pela ia",
  "achado musculoesqueletico em avaliacao",
  "achado musculoesquelético em avaliação",
  "a ia organiza possibilidades",
  "decisao final e do medico",
  "decisão final é do médico",
  "conforme padrao da lesao",
  "conforme padrão da lesão",
  "sistema compativel",
  "sistema compatível",
  "conteudo nao gerado",
  "conteúdo não gerado",
];

const normalizeSignal = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const cleanText = (value: unknown) =>
  String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .trim();

const hasForbiddenText = (value: unknown) => {
  const normalized = normalizeSignal(value);
  return !normalized || forbiddenPhrases.some((phrase) => normalized.includes(normalizeSignal(phrase)));
};

const firstText = (...values: unknown[]) => {
  for (const value of values) {
    const text = cleanText(value);
    if (text && !hasForbiddenText(text)) return text;
  }
  return "";
};

const toStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => toStringList(item))
      .map(cleanText)
      .filter((item) => item && !hasForbiddenText(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>)
      .flatMap((item) => toStringList(item))
      .map(cleanText)
      .filter((item) => item && !hasForbiddenText(item));
  }

  const text = cleanText(value);
  if (!text || hasForbiddenText(text)) return [];
  return text
    .split(/\n|;|\|/)
    .map((item) => item.replace(/^[-*\d.)\s]+/, "").trim())
    .filter((item) => item && !hasForbiddenText(item));
};

const evidenceUrlIsValid = (value?: string) => {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};

const normalizeEvidence = (value: unknown): EvidenceItemV2[] => {
  const items = Array.isArray(value) ? value : value ? [value] : [];
  return items
    .map((item): EvidenceItemV2 | null => {
      if (typeof item === "string") {
        const title = cleanText(item);
        return title && !hasForbiddenText(title) ? { title, relevance: "Referencia relacionada ao planejamento apresentado." } : null;
      }

      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, unknown>;
      const title = firstText(record.title, record.name, record.citation);
      if (!title) return null;

      const doi = cleanText(record.doi);
      const url = cleanText(record.url || record.link || record.pubmedUrl);
      return {
        title,
        authors: firstText(record.authors, record.author),
        journal: firstText(record.journal, record.source),
        year: firstText(record.year, record.publicationYear),
        doi,
        url: evidenceUrlIsValid(url) ? url : doi ? `https://doi.org/${doi.replace(/^doi:\s*/i, "")}` : undefined,
        relevance: firstText(record.relevance, record.summary, record.reason) || "Aplicavel ao padrao de lesao ou tecnica descrita.",
      };
    })
    .filter((item): item is EvidenceItemV2 => Boolean(item));
};

const normalizeTechnique = (value: unknown): SurgicalTechniqueV2 | null => {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const title = firstText(record.title, record.name, record.technique, record.label);
  if (!title) return null;

  return {
    title,
    indication: toStringList(record.indication || record.indications),
    whenToChoose: toStringList(record.whenToChoose || record.selectionCriteria || record.criteria),
    positioning: toStringList(record.positioning || record.patientPositioning),
    surgicalApproach: toStringList(record.surgicalApproach || record.approach || record.access),
    stepByStepTechnique: toStringList(record.stepByStepTechnique || record.surgicalSteps || record.operativeSteps || record.stepByStep || record.operativeSequence || record.steps),
    implantsAndMaterials: toStringList(record.implantsAndMaterials || record.materials || record.requiredMaterials || record.surgicalMaterials || record.implants),
    advantages: toStringList(record.advantages || record.benefits),
    limitationsAndRisks: toStringList(record.limitationsAndRisks || record.limitations || record.risks || record.risksAndPitfalls),
    possibleComplications: toStringList(record.possibleComplications || record.complications),
    consolidationTime: toStringList(record.consolidationTime || record.averageHealingTime || record.recoveryTime || record.healingTime),
    planB: toStringList(record.planB || record.contingencyPlan || record.intraoperativeContingencies || record.rescueStrategy),
    evidence: normalizeEvidence(record.evidence || record.scientificEvidence || record.references || record.citations),
  };
};

export const normalizeSurgicalPlanV2 = (raw: unknown): SurgicalPlanV2 | null => {
  if (!raw || typeof raw !== "object") return null;
  const input = raw as Record<string, unknown>;
  const management = (input.managementDecision || input.management || input.treatmentDecision || {}) as Record<string, unknown>;
  const decisionType = normalizeSignal(management.type || input.managementType || input.recommendation);
  const surgicalTechniques = (Array.isArray(input.surgicalTechniques) ? input.surgicalTechniques : Array.isArray(input.techniques) ? input.techniques : [])
    .map(normalizeTechnique)
    .filter((item): item is SurgicalTechniqueV2 => Boolean(item));
  const conservative = (input.conservativeTreatment || input.conservativePlan || input.conservativeManagement || {}) as Record<string, unknown>;
  const postoperative = (input.postoperativeCare || input.postoperativeProtocol || {}) as Record<string, unknown>;

  return {
    diagnosis: firstText(input.diagnosis, input.mainDiagnosis, input.primaryDiagnosis, input.diagnostico),
    anatomicalLocation: firstText(input.anatomicalLocation, input.location, input.localization, input.localizacaoAnatomica),
    aoOtaClassification: firstText(input.aoOtaClassification, input.aoOta, input.classificationCode, (input.classification as any)?.code, input.fractureClassification),
    managementDecision: {
      type: decisionType.includes("conserv") ? "conservative" : decisionType.includes("indeterm") || decisionType.includes("uncertain") ? "indeterminate" : "surgical",
      rationale: firstText(management.rationale, management.reasoning, input.surgicalIndicationReason, input.clinicalInterpretation),
      surgicalCriteria: toStringList(management.surgicalCriteria),
      conservativeCriteria: toStringList(management.conservativeCriteria),
    },
    surgicalTechniques,
    conservativeTreatment: {
      indication: toStringList(conservative.indication),
      immobilizationOrProtection: toStringList(conservative.immobilizationOrProtection || conservative.immobilization || conservative.protection),
      medication: toStringList(conservative.medication || conservative.medications),
      rehabilitation: toStringList(conservative.rehabilitation || conservative.physiotherapy),
      followUp: toStringList(conservative.followUp || conservative.followUpImaging),
      expectedRecovery: toStringList(conservative.expectedRecovery || conservative.recoveryTime),
      failureCriteria: toStringList(conservative.failureCriteria),
      conversionToSurgery: toStringList(conservative.conversionToSurgery || conservative.conversionCriteria),
    },
    postoperativeCare: {
      immobilization: toStringList(postoperative.immobilization),
      rangeOfMotion: toStringList(postoperative.rangeOfMotion || postoperative.adm),
      physiotherapy: toStringList(postoperative.physiotherapy || postoperative.rehabilitation),
      weightBearing: toStringList(postoperative.weightBearing || postoperative.loadProgression),
      followUpImaging: toStringList(postoperative.followUpImaging || postoperative.radiographicControl),
      returnToFunction: toStringList(postoperative.returnToFunction || postoperative.functionalReturn),
      redFlags: toStringList(postoperative.redFlags),
    },
    intraoperativeContingencyPlan: toStringList(input.intraoperativeContingencyPlan || input.planB || input.contingencyPlan),
    differentialDiagnosisAndAssociatedInjuries: toStringList(input.differentialDiagnosisAndAssociatedInjuries || input.differentialDiagnosis || input.differentialDiagnoses || input.associatedInjuries),
    globalEvidence: normalizeEvidence(input.globalEvidence || input.scientificEvidence || input.evidence || input.references || input.citations),
  };
};

export const validateSurgicalPlanV2 = (plan: SurgicalPlanV2 | null): SurgicalPlanV2Validation => {
  const reasons: string[] = [];
  if (!plan) return { valid: false, repairable: true, reasons: ["invalid_json"] };
  if (!plan.diagnosis || hasForbiddenText(plan.diagnosis)) reasons.push("diagnosis_missing_or_generic");
  if (!plan.anatomicalLocation || hasForbiddenText(plan.anatomicalLocation)) reasons.push("anatomical_location_missing_or_generic");
  if (!plan.managementDecision?.type) reasons.push("management_decision_missing");

  const permitsSurgery = plan.managementDecision.type === "surgical" || plan.managementDecision.type === "indeterminate";
  if (permitsSurgery) {
    if (plan.surgicalTechniques.length < 2) reasons.push("less_than_two_surgical_techniques");
    plan.surgicalTechniques.slice(0, 2).forEach((technique, index) => {
      if (!technique.title || hasForbiddenText(technique.title)) reasons.push(`technique_${index + 1}_title_missing_or_generic`);
      if (technique.stepByStepTechnique.length < 8) reasons.push(`technique_${index + 1}_step_by_step_too_short`);
      if (technique.implantsAndMaterials.length < 5) reasons.push(`technique_${index + 1}_materials_too_short`);
    });
  }

  if (plan.managementDecision.type === "conservative" || plan.managementDecision.type === "indeterminate") {
    const conservativeCount = [
      ...plan.conservativeTreatment.indication,
      ...plan.conservativeTreatment.rehabilitation,
      ...plan.conservativeTreatment.followUp,
      ...plan.conservativeTreatment.failureCriteria,
    ].length;
    if (conservativeCount < 6) reasons.push("conservative_treatment_incomplete");
  }

  const postopCount = Object.values(plan.postoperativeCare).flat().length;
  if (permitsSurgery && postopCount < 6) reasons.push("postoperative_care_incomplete");
  if (permitsSurgery && plan.intraoperativeContingencyPlan.length < 4) reasons.push("contingency_plan_incomplete");
  if (plan.differentialDiagnosisAndAssociatedInjuries.length < 3) reasons.push("differentials_incomplete");

  return { valid: reasons.length === 0, repairable: true, reasons };
};

export const summarizeSurgicalPlanV2 = (plan: SurgicalPlanV2 | null) => ({
  diagnosis: plan?.diagnosis || "",
  anatomicalLocation: plan?.anatomicalLocation || "",
  aoOtaClassification: plan?.aoOtaClassification || "",
  managementType: plan?.managementDecision?.type || "",
  techniques: plan?.surgicalTechniques?.length || 0,
  postoperativeItems: plan ? Object.values(plan.postoperativeCare).flat().length : 0,
  contingencyItems: plan?.intraoperativeContingencyPlan?.length || 0,
  differentialItems: plan?.differentialDiagnosisAndAssociatedInjuries?.length || 0,
});
